import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Tab,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useApplications, useUserApplications } from '../../hooks/useApplications';
import { usePermissions } from '../../hooks/useAuth';
import LoadingOverlay from '../Shared/LoadingOverlay';

import { districtService } from '../../services/districtService';

const STATUS_COLORS = {
  'submitted': 'info',
  'under_review': 'warning',
  'approved': 'success',
  'completed': 'success',
  'rejected': 'error',
  'pending_inspection': 'warning',
  'inspection_completed': 'info',
  'payment_processed': 'success',
  'processing': 'primary',
  'cheque_prepared': 'warning',
};

const ApplicationsList = () => {
  const { listtype } = useParams();

  const navigate = useNavigate();
  const { username, role, id:curr_user_id, permissions, isAuthenticated, district } = useSelector((state) => state.auth);
  const { hasPermission } = usePermissions();
  
  // State for pagination and filtering
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchFilters, setSearchFilters] = useState([
    { id: 1, field: 'application_refno', value: '' }
  ]);
  const [debouncedFilters, setDebouncedFilters] = useState(searchFilters);

  const [subdivisions, setSubdivisions] = useState([]);

  const [filteredSubdivision, setFilteredSubdivision] = useState('');

  const [filteredDamageType, setFilteredDamageType] = useState('');

  const damageTypeOptions = {
    'land': 'Land Damage',
    'house': 'House Damage',
    'livestock': 'Livestock Loss',
    'crop': 'Crop Damage',
  }

  const filterStatusOptions = {
    'SUBMITTED': 'Submitted',
    'UNDER_REVIEW': 'Under Review',
    'PROCESSING': 'Processing',
    'INITIATED_DAMAGE_INSPECTION': 'Field Inspection Report Pending',
    'FIELD_INSPECTION_VERIFIED': 'Proceed to Calculate Ex gratia',
    'CALCULATING_EXGRATIA_AMOUNT': 'Auto Calculated Exgratia',
    'APPROVED': 'Approved',
    'REJECTED': 'Rejected',
    'CHEQUE_PREPARED': 'Cheque Prepared',
    'COMPLETED': 'Completed',
  }

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFromUrl = params.get('status');

    if (statusFromUrl && Object.prototype.hasOwnProperty.call(filterStatusOptions, statusFromUrl)) {
      setFilterStatus(statusFromUrl);
      setPage(0);
    }
  }, [location.search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(searchFilters);
      setPage(0); // Reset to first page when search changes
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchFilters]);

  useEffect(() => {
    const fetchSubdivisions = async () => {
      try {
        const data = await districtService.getSubdivisions('code', district);
        setSubdivisions(data);
      } catch (error) {
        console.error('Error fetching subdivisions for filter:', error);
      }
    };
    fetchSubdivisions();
  }, []);

  const searchFieldOptions = [
    { value: 'application_refno', label: 'Reference ID' },
    { value: 'applicant_name', label: 'Applicant Name' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'ph_number', label: 'Phone Number' },
    { value: 'land_khatian_number', label: 'Land Khatian Number' },
    { value: 'land_plot_nos', label: 'Land Plot Numbers' },
  ];

  // Prepare API parameters
  const apiParams = {
    page: page + 1, // API is 1-indexed
    per_page: rowsPerPage,
    status: filterStatus,
    subdivision: filteredSubdivision || undefined,
    damage_type: filteredDamageType
  };

  // Build search_by object from filters
  const searchByObj = {};
  debouncedFilters.forEach(filter => {
    if (filter.value && filter.value.trim()) {
      searchByObj[filter.field] = filter.value.trim();
    }
  });

  // Add search_by as JSON string if there are any filters
  if (Object.keys(searchByObj).length > 0) {
    apiParams.search_by = JSON.stringify(searchByObj);
  }

  // Use appropriate hook based on user role
  const isCitizenOrL1Staff = role?.name === 'citizen' || role?.name === 'l1_staff';

  const ROLES_SHOW_FILTERS_SUBDIV = ['dpo', 'dc', 'admin', 'ahvs_h', 'horti_h', 'agri_h', 'accounts'];

  const showSubdivisionFilter = ROLES_SHOW_FILTERS_SUBDIV.includes(role?.name);
  
  // Use only the appropriate hook - conditional hook calling
  let applicationsQuery;
  if (isCitizenOrL1Staff) {
    applicationsQuery = useUserApplications(apiParams);
  } else {
    if (listtype === 'assigned') {
      apiParams.list_type = listtype;
    }
    else if (listtype === 'newapplications') {
      apiParams.list_type = listtype;
    }
    applicationsQuery = useApplications({ 
      ...apiParams, 
      limit: rowsPerPage // Staff endpoint uses 'limit' instead of 'per_page'
    });
  }
  
  const { data: applicationsData, isLoading, error, refetch } = applicationsQuery;

  const applications = applicationsData?.applications || [];
  const totalCount = applicationsData?.total || 0;

  // Calculate max page and ensure current page is valid
  const maxPage = Math.max(0, Math.ceil(totalCount / rowsPerPage) - 1);
  const validPage = Math.min(page, maxPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (id, field, value) => {
    setSearchFilters(prevFilters =>
      prevFilters.map(filter =>
        filter.id === id ? { ...filter, [field]: value } : filter
      )
    );
  };

  const handleAddFilter = () => {
    const newId = Math.max(...searchFilters.map(f => f.id), 0) + 1;
    const usedFields = searchFilters.map(f => f.field);
    const availableField = searchFieldOptions.find(option => !usedFields.includes(option.value));
    
    if (availableField) {
      setSearchFilters([...searchFilters, { id: newId, field: availableField.value, value: '' }]);
    }
  };

  const handleRemoveFilter = (id) => {
    if (searchFilters.length > 1) {
      setSearchFilters(prevFilters => prevFilters.filter(filter => filter.id !== id));
    }
  };

  const handleClearAllFilters = () => {
    setSearchFilters([{ id: 1, field: 'application_refno', value: '' }]);
    setFilterStatus('');
    setFilteredSubdivision('');
    setFilteredDamageType('');
    setPage(0);
  };

  const handleView = (applicationRefno) => {
    // navigate(`/applications/details/${applicationRefno}`);
    window.open(`/applications/details/${applicationRefno}`, '_blank', 'noopener,noreferrer');
  };

  const handleEdit = (applicationRefno) => {
    navigate(`/applications/edit/${applicationRefno}`);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      return new Date(dateTimeString).toLocaleString('en-IN');
    } catch {
      return dateTimeString;
    }
  };

  const formatSnakeCaseToTitle = (str) => {
    if (!str) return '';
    return str.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={() => refetch()}>
          Retry
        </Button>
      }>
        Error loading applications: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          {isCitizenOrL1Staff ? 'My Applications' : 'Ex-Gratia Applications'}
        </Typography>
        
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          {hasPermission('apply_exgratia') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/applications/new')}
            >
              New Application
            </Button>
          )}
        </Box>
      </Box>

      {/* Citizen Information Banner */}
      {isCitizenOrL1Staff && applications.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2">
              <strong>Your Applications Summary:</strong> Total applications: {totalCount} • 
              Submitted: {applications.filter(app => app.status === 'SUBMITTED').length} • 
              In Review: {applications.filter(app => ['UNDER_REVIEW', 'PENDING_INSPECTION'].includes(app.status)).length} • 
              Completed: {applications.filter(app => ['APPROVED', 'PAYMENT_PROCESSED'].includes(app.status)).length}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Citizen Information Banner for new users */}
      {isCitizenOrL1Staff && applications.length === 0 && (
        <Card sx={{ mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2">
              <strong>Welcome to Ex-Gratia Management System!</strong> You haven't submitted any applications yet. 
              Click "New Application" above to apply for ex-gratia assistance for natural calamity damages.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Search & Filter
            </Typography>
            <Box display="flex" gap={1}>
              {(searchFilters.some(f => f.value) || filterStatus || filteredSubdivision || filteredDamageType) && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearAllFilters}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </Box>

          {/* Search Filters */}
          <Grid container spacing={2} alignItems="center">
            {searchFilters.map((filter, index) => (
              <React.Fragment key={filter.id}>
                <Grid size={{ xs: 12, sm: 5, md: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Search Field</InputLabel>
                    <Select
                      value={filter.field}
                      label="Search Field"
                      onChange={(e) => handleFilterChange(filter.id, 'field', e.target.value)}
                    >
                      {searchFieldOptions.map(option => (
                        <MenuItem 
                          key={option.value} 
                          value={option.value}
                          disabled={searchFilters.some(f => f.id !== filter.id && f.field === option.value)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 8 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Enter ${searchFieldOptions.find(o => o.value === filter.field)?.label || 'value'}...`}
                    value={filter.value}
                    onChange={(e) => handleFilterChange(filter.id, 'value', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 1, md: 1 }}>
                  <Box display="flex" gap={1}>
                    {index === searchFilters.length - 1 && searchFilters.length < searchFieldOptions.length && (
                      <Tooltip title="Add filter">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleAddFilter}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {searchFilters.length > 1 && (
                      <Tooltip title="Remove filter">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveFilter(filter.id)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          {/* Status Filter */}
          <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              {
                listtype !== 'newapplications' && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Filter by Status"
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setPage(0);
                      }}
                      slotProps={{ select: { native: true } }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="">All</option>
                      {Object.entries(filterStatusOptions).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                )
              }

              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Damage Type"
                  value={filteredDamageType ? filteredDamageType : ''}
                  onChange={(e) => {
                    setFilteredDamageType(e.target.value);
                    setPage(0);
                  }}
                  slotProps={{ select: { native: true } }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">All</option>
                  {Object.entries(damageTypeOptions).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </TextField>
              </Grid>
              {
                (showSubdivisionFilter) && (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label="Subdivision"
                      value={filteredSubdivision}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : '';
                        setFilteredSubdivision(value);
                        setPage(0);
                      }}
                      slotProps={{ select: { native: true } }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <option value="">All</option>
                      {subdivisions.map((subdivision) => (
                        <option key={subdivision.id} value={subdivision.id}>
                          {subdivision.name}
                        </option>
                      ))}
                    </TextField>
                  </Grid>
                )

              }
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  {isLoading ? 'Loading...' : `${totalCount} applications found`}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {/* Applications Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Reference No.</TableCell>
                <TableCell>Applicant</TableCell>
                <TableCell>Village</TableCell>
                <TableCell>District</TableCell>
                <TableCell>SD | Block</TableCell>
                <TableCell>Damage Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>ExGratia Amt.</TableCell>

                {/* <TableCell>Applied Date</TableCell>
                <TableCell>NC Date</TableCell> */}
                <TableCell>Currently With</TableCell>
                {/* <TableCell align="center">Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {isCitizenOrL1Staff 
                        ? "You haven't submitted any applications yet. Click 'New Application' to get started." 
                        : "No applications found"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                applications.filter(app => 
                  (filterStatus && filterStatus !== ''
                    ? (filterStatus == 'INITIATED_DAMAGE_INSPECTION' 
                        ? (app.can_calculate_exgratia[0] === false && app.can_calculate_exgratia[1] === true) 
                        : ( filterStatus == 'FIELD_INSPECTION_VERIFIED'
                            ? (app.can_calculate_exgratia[0] === true && app.can_calculate_exgratia[1] === true)
                            : true
                          )
                      )
                    : true
                  )
                ).map((application) => (
                  <Tooltip 
                    key={application.application_refno || application.id}
                    title={
                      application.remarks ? (
                        <Box>
                          <Typography variant="subtitle2" color="primary.light">Applicant Remarks</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                            {application.remarks}
                          </Typography>
                          <Divider sx={{ borderColor: 'grey.500', pt: 1 }} />
                          <Typography variant="caption" sx={{ color: 'custom.greenyellow' }}>
                            Applied on: {formatDateTime(application.created_at)}
                          </Typography>
                          <Divider sx={{ borderColor: 'grey.500' }} />
                          <Typography variant="caption" sx={{ color: 'custom.greenyellow' }}>
                            NC Date: {formatDateTime(application.actual_nc_datetime)}
                          </Typography>
                        </Box>
                      ) : ''
                    }
                    arrow
                    placement="top-start"
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: '#222',
                          color: '#fff',
                          fontSize: 14,
                          maxWidth: 300,
                          p: 1
                        }
                      }
                    }}
                  >
                    <TableRow 
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleView(application.application_refno || application.id)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {application.application_refno || application.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {application.applicant_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {application.sodowo}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {application.village}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Ward: {application.ward.name}
                        </Typography>
                      </TableCell>
                      <TableCell>{application.district}</TableCell>
                      <TableCell>{application.ward.gpu.block.subdivision.name + ' | ' + application.ward.gpu.block.name}</TableCell>
                      <TableCell>
                        {application.damage_type?.slice(0, 2).map((type, index) => (
                          <Chip
                            key={index}
                            label={type}
                            size="small"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                        {application.damage_type?.length > 2 && (
                          <Chip
                            label={`+${application.damage_type.length - 2} more`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ flexDirection: 'column', display: 'flex' }}>
                          <Chip
                            label={formatSnakeCaseToTitle(application.status) || 'Submitted'}
                            color={STATUS_COLORS[application.status] || 'default'}
                            size="small"
                            sx={{ borderRadius: '10px 10px 0 0' }}
                          />
                          {
                            application.can_calculate_exgratia[0] && application.can_calculate_exgratia[1]
                            ? (
                              <Tooltip title="Ex-gratia calculation can be initiated">
                                <Chip
                                  label="Proceed to calculate Ex-gratia"
                                  size="small"
                                  color='success'
                                  sx={{ borderRadius: '0 0 10px 10px' }}
                                />
                              </Tooltip>
                            ) : !application.can_calculate_exgratia[0] && application.has_field_inspection_reports
                                ? (
                                    <Tooltip title="Waiting for all field inspections to be verified">
                                      <Chip
                                        label="Pending Report Verification"
                                        size="small"
                                        color='info'
                                        sx={{ borderRadius: '0 0 10px 10px' }}
                                      />
                                    </Tooltip>
                                  ) : application.is_exgratia_calculation_initiated 
                                        && (application.status == 'calculating_exgratia_amount') 
                                        ? (
                                            <Tooltip title="Ex-gratia calculation has been initiated">
                                              <Chip
                                                label="Ex-gratia calculated"
                                                size="small"
                                                color='primary'
                                                sx={{ borderRadius: '0 0 10px 10px' }}
                                              />
                                            </Tooltip>
                                          ) : application.status == 'completed' ? (
                                            <Tooltip title="Ex-gratia calculation has been completed">
                                              <Chip
                                                label="Ex-gratia completed"
                                                size="small"
                                                color='success'
                                                sx={{ borderRadius: '0 0 10px 10px', borderTop: '2px solid', borderColor: '#d1ff33', fontWeight: 'bold' }}
                                              />
                                            </Tooltip>
                                          ) : null
                          }
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {application.final_exgratia_amount ? `₹ ${application.final_exgratia_amount.toLocaleString('en-IN')}` : 'N/A'}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        {formatDateTime(application.created_at)}
                      </TableCell>
                      <TableCell>
                        {formatDateTime(application.actual_nc_datetime)}
                      </TableCell> */}
                      <TableCell>
                        {application.assigned_to_designation || 'N/A'}
                      </TableCell>
                      {/* <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(application.application_refno || application.id)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {hasPermission('edit_exgratia') && application.status === 'SUBMITTED' && (
                            <Tooltip title="Edit Application">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(application.application_refno || application.id)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell> */}
                    </TableRow>
                  </Tooltip>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={validPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          disabled={isLoading}
        />
      </Card>

      {/* Status Guide for Citizens */}
      {isCitizenOrL1Staff && applications.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              Application Status Guide
            </Typography>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="SUBMITTED" color="info" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Under initial review</Typography>
                </Box>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="UNDER_REVIEW" color="warning" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Being processed by staff</Typography>
                </Box>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="PENDING_INSPECTION" color="warning" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Awaiting field inspection</Typography>
                </Box>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="APPROVED" color="success" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Approved for payment</Typography>
                </Box>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="PAYMENT_PROCESSED" color="success" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Payment completed</Typography>
                </Box>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="REJECTED" color="error" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Application declined</Typography>
                </Box>
              </Grid>
              <Grid xs={12} sm={6} md={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip label="RETURNED" color="secondary" size="small" sx={{ mr: 1 }} />
                  <Typography variant="caption">Needs your attention</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Loading Overlay */}
      <LoadingOverlay
        open={isLoading}
        message="Loading applications..."
      />
    </Box>
  );
};

export default ApplicationsList;
